/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IDictionary, IModule } from '@msdyn365-commerce/core-internal';
import { EXCEPTION_ERROR_CLIENT_BOUNDARY, InternalTelemetry, LogLevel } from '@msdyn365-commerce/telemetry-internal';
import { get as _get } from 'lodash';
import React, { useMemo } from 'react';
import { MODULE_CONFIG_ERRORS_KEY } from '../consts';
import { renderView } from './render-view';
import { TSafeRenderModuleProps, TSafeRenderModuleState } from './safe-render-module';

type ErrorType = 'empty' | 'unregistered' | 'error';

interface IErrorModuleProps extends IModule {
    id: string;
    errorType: ErrorType;
    error: Error;
    message?: string;
}

const getStandardErrorModuleMarkup = (props: IErrorModuleProps) => {
    return (
        <div className='error-module card mb-3' id={props.id} {...props.renderModuleAttributes(props)}>
            <div className='card-header text-white bg-danger'>{props.id}&apos; threw exception</div>
            <div className='card-body'>
                <p className='card-text'>
                    <br></br>
                    {props.error && props.error.toString()}
                </p>
                <p className='card-text'>
                    <br></br>
                    {props.message}
                </p>
            </div>
        </div>
    );
};

/**
 * The error module
 */
export const ErrorModule: React.SFC<IErrorModuleProps> = (props: IErrorModuleProps) => {
    const params = _get(props, 'context.request.params', {});
    const { isDebug } = params;
    return isDebug || process.env.NODE_ENV === 'development' ? useMemo(() => getStandardErrorModuleMarkup(props), [props]) : null;
};

/* eslint-disable @typescript-eslint/no-explicit-any */
export const renderErrorOrPlaceHolderModules = (
    err: Error,
    internalTelemetry: InternalTelemetry,
    mappedProps: any,
    state: TSafeRenderModuleState,
    props: TSafeRenderModuleProps,
    isEditor: boolean,
    renderModuleAttributes: (props: { [x: string]: any }) => IDictionary<string>
): JSX.Element | undefined => {
    if (!isEditor) {
        internalTelemetry.log(LogLevel.Error, EXCEPTION_ERROR_CLIENT_BOUNDARY, { exception: err });
        return (
            <ErrorModule
                {...mappedProps}
                errorType={state.error ? 'error' : 'empty'}
                error={err}
                message={state.info && state.info.componentStack}
                renderModuleAttributes={renderModuleAttributes}
                renderView={renderView}
            />
        );
    }
    /* The following logic helps us retry rendering the component if it previously failed in editor scenario
           There are two cases where we should render the placeholder instead of trying to render the component when authoring

            1. If error state is set because the module threw an error from rendering.
            In this case the module will have an entry in the cache for errors that was set in componentShouldUpdate.
            When a module is ready to retry rendering, clearing it's MODULE_CONFIG_ERRORS_KEY entry will force a retry.
            Should the component fail to render again, the state and cache entry will be re-updated with error(s)

            2. If the module has known config errors, then we should show the placeholder div

        */
    let moduleError = props.store.requestCache.get({ typeName: MODULE_CONFIG_ERRORS_KEY, key: props.id });
    if (state.error && moduleError.item && moduleError.item.length > 0) {
        moduleError = [{ property: props.id, message: err.message }];
    } else if (mappedProps.configErrors && mappedProps.configErrors.length > 0) {
        moduleError = mappedProps.configErrors;
    }
    // In an editor scenario, if add/remove module without server refresh feature is enabled
    // SDK will have to place the placeholder module instead of OneRF
    if (
        isEditor &&
        moduleError &&
        moduleError.length > 0 &&
        _get(props, ['context', 'request', 'features', 'enableAuthoringRemoveAndAddModule'], false)
    ) {
        // error module doesnt render 'click here to configure' placeholder module anymore
        // an empty div with module markers data-i attributes is rendered instead
        const dataInfo = [`id:${mappedProps.id}`];
        mappedProps.slotId && dataInfo.push(`s:${mappedProps.slotId}`);
        mappedProps.parentId && dataInfo.push(`p:${mappedProps.parentId}`);
        mappedProps.typeName && dataInfo.push(`t:${mappedProps.typeName}`);
        return <div id={`placeholder_${props.id}`} data-i={`{${dataInfo.join(',')}}`} />;
    }
    return undefined;
};
